'use client';
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
    ReactNode,
} from 'react';


export type CommandTeamDrawerPanel = 'command' | 'platform';

interface CommandTeamDrawerContextValue {
    drawerOpen: boolean;
    panel: CommandTeamDrawerPanel;
    openDrawer: () => void;
    closeDrawer: () => void;
    expandPlatformNav: () => void;
    backToCommandPanel: () => void;
}

const CommandTeamDrawerContext = createContext<CommandTeamDrawerContextValue | null>(null);

export function CommandTeamDrawerProvider({ children }: { children: ReactNode }) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [panel, setPanel] = useState<CommandTeamDrawerPanel>('command');

    const openDrawer = useCallback(() => {
        setPanel('command');
        setDrawerOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
        setDrawerOpen(false);
        setPanel('command');
    }, []);

    const expandPlatformNav = useCallback(() => {
        setPanel('platform');
    }, []);

    const backToCommandPanel = useCallback(() => {
        setPanel('command');
    }, []);

    const value = useMemo(
        () => ({
            drawerOpen,
            panel,
            openDrawer,
            closeDrawer,
            expandPlatformNav,
            backToCommandPanel,
        }),
        [drawerOpen, panel, openDrawer, closeDrawer, expandPlatformNav, backToCommandPanel],
    );

    return (
        <CommandTeamDrawerContext.Provider value={value}>{children}</CommandTeamDrawerContext.Provider>
    );
}

export function useCommandTeamDrawer(): CommandTeamDrawerContextValue {
    const ctx = useContext(CommandTeamDrawerContext);
    if (!ctx) {
        throw new Error('useCommandTeamDrawer must be used within CommandTeamDrawerProvider');
    }
    return ctx;
}


export function useCommandTeamDrawerOptional(): CommandTeamDrawerContextValue | null {
    return useContext(CommandTeamDrawerContext);
}

interface SidebarContextType {
    sidebarOpen: boolean;
    toggleSidebar: () => void;
    setSidebarOpen: (open: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);


const WIDE_DESKTOP_MIN_WIDTH_PX = 1440;

interface SidebarProviderProps {
    children: ReactNode;
    /** Initial open state before viewport rules run. */
    defaultOpen?: boolean;
    /** When false, wide desktop does not auto-expand the command sidebar. */
    autoExpandOnWideDesktop?: boolean;
}

export function SidebarProvider({
    children,
    defaultOpen = true,
    autoExpandOnWideDesktop = true,
}: SidebarProviderProps) {
    const [sidebarOpen, setSidebarOpen] = useState(defaultOpen);
    const isManualToggleRef = useRef(false);
    const wasManuallyClosedRef = useRef(false);
    const sidebarOpenRef = useRef(defaultOpen);
    const autoExpandOnWideDesktopRef = useRef(autoExpandOnWideDesktop);

    useEffect(() => {
        autoExpandOnWideDesktopRef.current = autoExpandOnWideDesktop;
    }, [autoExpandOnWideDesktop]);

    // Keep ref in sync with state
    useEffect(() => {
        sidebarOpenRef.current = sidebarOpen;
    }, [sidebarOpen]);

    // Auto-close sidebar based on screen size
    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            const isPhone = width < 768;

            if (isPhone) {
               
                if (isManualToggleRef.current && !sidebarOpenRef.current) {
                    wasManuallyClosedRef.current = true;
                }
                if (sidebarOpenRef.current) {
                    setSidebarOpen(false);
                }
            } else if (width < WIDE_DESKTOP_MIN_WIDTH_PX) {
               
                if (!isManualToggleRef.current) {
                    setSidebarOpen(false);
                } else if (!sidebarOpenRef.current) {
                    wasManuallyClosedRef.current = true;
                } else {
                    wasManuallyClosedRef.current = false;
                }
            } else {
                // Wide desktop: expanded by default unless user previously minimized for this session band.
                if (
                    autoExpandOnWideDesktopRef.current &&
                    !isManualToggleRef.current &&
                    !wasManuallyClosedRef.current
                ) {
                    setSidebarOpen(true);
                } else if (!isManualToggleRef.current) {
                    /* keep current */
                } else if (!sidebarOpenRef.current) {
                    wasManuallyClosedRef.current = true;
                } else {
                    wasManuallyClosedRef.current = false;
                }
            }

            // Reset manual toggle flag after handling resize
            isManualToggleRef.current = false;
        };

        // Check on mount
        checkScreenSize();

        // Listen for resize events with debounce — ignore height-only changes
        // (virtual keyboard open/close on mobile fires resize but only changes height)
        let resizeTimeout: NodeJS.Timeout;
        let prevWidth = window.innerWidth;
        const handleResize = () => {
            const currentWidth = window.innerWidth;
            if (currentWidth === prevWidth) return;
            prevWidth = currentWidth;
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(checkScreenSize, 150);
        };

        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, []);

    const toggleSidebar = () => {
        isManualToggleRef.current = true;
        setSidebarOpen(prev => !prev);
    };

    const setSidebarOpenWithFlag = (open: boolean) => {
        isManualToggleRef.current = true;
        setSidebarOpen(open);
    };

    return (
        <SidebarContext.Provider value={{ sidebarOpen, toggleSidebar, setSidebarOpen: setSidebarOpenWithFlag }}>
            {children}
        </SidebarContext.Provider>
    );
}

export function useSidebar() {
    const context = useContext(SidebarContext);
    if (context === undefined) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
}

